// What the conversation screens read again when something happens elsewhere.
// Team events (a workspace's topics, messages, direct messages, emoji, pins)
// come through the team stream; personal direct messages through Me's stream
// (they belong to no workspace). Payloads carry ids only: the handlers drop
// the queries those ids name (a message's topic, a space's pins), and the
// small lists the change shows in (inbox, unreads, the badge). The topic
// itself is read alone and put where it shows (`refreshTopic`): the topics'
// pages and the other topics are never read again for an event.
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { type Api, badgeChanged } from "@trameria/ui-core";
import type { TeamRealtimeHandler, TeamRealtimeHandlers } from "../workspace";
import {
  type ConvPlace,
  dmsKey,
  emojisKey,
  inboxKey,
  messagesKey,
  overviewKey,
  overviewPrefix,
  PERSONAL_DMS,
  refreshTopic,
  unreadsKey,
  workspaceConvPlace,
} from "./queries";

const str = (v: unknown) => (typeof v === "string" ? v : null);

const drop = (qc: QueryClient, keys: readonly QueryKey[]) => {
  for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
};

/**
 * The small lists a workspace's conversation shows in, and the app's badge
 * (which counts unread ones, with Me's Inbox under it): read again while not
 * shown too, so they are right when the person comes back to them.
 */
const dropLists = (qc: QueryClient, slug: string) => {
  for (const queryKey of [inboxKey(slug), unreadsKey(slug)])
    void qc.invalidateQueries({ queryKey, refetchType: "all" });
  badgeChanged(qc);
};

/**
 * A handler for one workspace's conversation queries: drops what `fn` names,
 * with `lists` the small lists (`dropLists`), and with `topic` reads the
 * payload's topic again.
 */
const inWorkspace =
  (
    fn: (
      slug: string,
      place: ConvPlace,
      payload: Record<string, unknown>,
    ) => QueryKey[],
    options: { topic?: boolean; lists?: boolean } = {},
  ): TeamRealtimeHandler =>
  (payload, { qc, api, event, slugOf }) => {
    const slug = slugOf(event.workspaceId);
    if (slug === null) return;
    const place = workspaceConvPlace(slug);
    drop(qc, fn(slug, place, payload));
    if (options.lists) dropLists(qc, slug);
    const topicId = str(payload.topicId);
    if (options.topic && topicId) void refreshTopic(qc, api, place, topicId);
  };

export const conversationTeamHandlers: TeamRealtimeHandlers = {
  "topic.created": inWorkspace(() => [], { topic: true, lists: true }),
  // Renamed or moved: the overviews of both spaces (the old one is not named).
  "topic.changed": inWorkspace((slug) => [overviewPrefix(slug)], {
    topic: true,
    lists: true,
  }),
  "message.created": inWorkspace(
    (_slug, place, p) => {
      const topicId = str(p.topicId);
      return topicId ? [messagesKey(place, topicId)] : [];
    },
    { topic: true, lists: true },
  ),
  "message.changed": inWorkspace((_slug, place, p) => {
    const topicId = str(p.topicId);
    return topicId ? [messagesKey(place, topicId)] : [];
  }),
  "read.changed": inWorkspace(() => [], { topic: true, lists: true }),
  "emoji.changed": inWorkspace((slug) => [emojisKey(slug)]),
  "pin.changed": inWorkspace((slug, _place, p) => {
    const spaceId = str(p.spaceId);
    return spaceId ? [overviewKey(slug, spaceId)] : [];
  }),
};

const personal = (payload: unknown, qc: QueryClient, api: Api) => {
  const topicId = str((payload as Record<string, unknown> | null)?.topicId);
  drop(qc, [
    ...(topicId
      ? [messagesKey(PERSONAL_DMS, topicId)]
      : [dmsKey(PERSONAL_DMS)]),
  ]);
  badgeChanged(qc);
  if (topicId) void refreshTopic(qc, api, PERSONAL_DMS, topicId);
};

/** Me's events of the personal direct messages. */
export const personalDmHandlers = {
  "dm.message": (
    payload: unknown,
    { qc, api }: { qc: QueryClient; api: Api },
  ) => personal(payload, qc, api),
  "dm.changed": (
    payload: unknown,
    { qc, api }: { qc: QueryClient; api: Api },
  ) => personal(payload, qc, api),
};
